/**
 * SearchInputComponent (search-with-tabs specific)
 *
 * Controlled search input with debounced onSearch callback and a small UX guard
 * that prevents a lone "!" from triggering a search (shows info alert instead).
 * Exposes an imperative API via forwardRef to focus() and clear() the input.
 *
 * Props
 * - placeholder?: string — input placeholder
 * - isReset?: boolean — when toggled, clears the input
 * - onSearch: (query: string) => void — debounced 300ms
 * - onFocus?: () => void — optional focus callback
 *
 * Notes
 * - Uses AlertComponent for transient info messaging
 */
import React, {
  useState,
  useEffect,
  useCallback,
  useImperativeHandle,
  forwardRef,
} from "react";
import Box from "@mui/material/Box";
import SearchIcon from "@mui/icons-material/Search";
import Divider from "@mui/material/Divider";
import { InputAdornment, OutlinedInput } from "@mui/material";
import { SearchInputComponentProps } from "./search-input-component.props";
import "./search-input-component.scss";
import CloseIcon from "@mui/icons-material/Close";
import AlertComponent from "../../alert-component/alert-component";
import { debounce } from "lodash";

const SearchInputComponent = forwardRef<
  { focus: () => void; clear: () => void },
  SearchInputComponentProps
>(({ placeholder = "", isReset, onSearch, onFocus }, ref) => {
  const [searchText, setSearchText] = useState("");
  const [alertOpen, setAlertOpen] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [alertType, setAlertType] = useState<
    "success" | "error" | "info" | "warning"
  >("success");

  useImperativeHandle(ref, () => ({
    focus: () => {
      const input = document.getElementById(
        "search-leads",
      ) as HTMLInputElement | null;
      input?.focus();
    },
    clear: () => {
      setSearchText("");
    },
  }));

  useEffect(() => {
    setSearchText("");
  }, [isReset]);

  const debouncedSearch = useCallback(
    debounce((value: string) => {
      if (value.trim() === "!") {
        setAlertMessage('Search cannot be performed with just "!"');
        setAlertType("info");
        setAlertOpen(true);
        return;
      }
      onSearch(value);
    }, 300),
    [onSearch],
  );

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setSearchText(value);
    debouncedSearch(value);
  };

  return (
    <Box>
      <AlertComponent
        message={alertMessage}
        type={alertType}
        open={alertOpen}
        onClose={() => setAlertOpen(false)}
      />
      <div className="form-search-input-component">
        <OutlinedInput
          id="search-leads"
          className="search-input"
          placeholder={placeholder}
          onChange={handleSearchChange}
          value={searchText}
          onFocus={onFocus}
          endAdornment={
            <InputAdornment position="end">
              {searchText && (
                <>
                  <CloseIcon
                    className="clear-icon"
                    onClick={() => {
                      setSearchText("");
                      onSearch("");
                    }}
                    style={{ cursor: "pointer", marginRight: 0 }}
                  />
                  <Divider
                    orientation="vertical"
                    flexItem
                    className="search-icon-divider"
                    style={{ marginLeft: "8px" }}
                  />
                </>
              )}
              <SearchIcon
                className="search-icon"
                titleAccess="Click to Search"
                style={{ cursor: "pointer", marginRight: "12px" }}
              />
            </InputAdornment>
          }
          fullWidth
          slotProps={{
            input: {
              style: {
                whiteSpace: "nowrap",
                overflow: "hidden !important",
                textOverflow: "ellipsis",
              },
            },
          }}
          sx={{
            "& input": {
              padding: "12px 30px",
              "&::placeholder": {
                color: "#6E6D92",
                opacity: 1,
              },
            },
            "& .MuiOutlinedInput-root": {
              height: "40px",
            },
            borderRadius: "8px",
            "& .MuiInputBase-input": {
              padding: "10px 30px",
            },
          }}
        />
      </div>
    </Box>
  );
});

SearchInputComponent.displayName = "SearchInputComponent";
export default SearchInputComponent;
